import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  input,
  OnInit,
  Signal,
  signal,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import {
  combineLatestWith,
  debounceTime,
  finalize,
  map,
  Observable,
  scan,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { FilterComponent } from '../filter.interfaces';
import { FilterService } from '../filter.service';
import {
  FilterCommand,
  FilterOperator,
  FilterSuffix,
} from '../../../api/api.operators';
import { MatIcon } from '@angular/material/icon';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ApplyPipe } from '../../apply.pipe';
import { FilterOperatorComponent } from '../filter-operator/filter-operator.component';

export interface MultiselectItem {
  id: string;
  label: string;
}

export type SearchFn = (
  search: string,
  page: number,
) => Observable<{ items: MultiselectItem[]; hasNextPage: boolean }>;

@Component({
  selector: 'app-multiselect-filter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatInputModule,
    MatIcon,
    ReactiveFormsModule,
    ApplyPipe,
    FilterOperatorComponent,
  ],
  providers: [],
  styleUrl: 'multiselect-filter.component.scss',
  templateUrl: './multiselect-filter.component.html',
})
export class MultiselectFilterComponent implements FilterComponent, OnInit {
  appFilter = input.required<string>();
  appFilterOperators = input<FilterOperator[]>();
  appFilterNotOperators = input<FilterOperator[]>();
  searchFn = input.required<SearchFn>();
  searchPage = signal(0);
  loading = signal(false);
  hasNextPage = signal(false);
  searchControl = new FormControl('', { nonNullable: true });
  searchItems: Signal<MultiselectItem[]> = toSignal(
    this.searchControl.valueChanges.pipe(
      startWith(''),
      tap(() => {
        this.searchPage.set(1);
        this.hasNextPage.set(true);
      }),
      combineLatestWith(
        toObservable(this.searchPage),
        toObservable(this.searchFn),
      ),
      tap(() => {
        this.loading.set(true);
      }),
      debounceTime(200),
      switchMap(([value, page, fn]) =>
        fn(value, page).pipe(
          finalize(() => {
            this.loading.set(false);
          }),
        ),
      ),
      tap(({ hasNextPage }) => this.hasNextPage.set(hasNextPage)),
      map(({ items }) => items),
      scan((previous, current) =>
        this.searchPage() == 1 ? current : [...previous, ...current],
      ),
    ),
    { initialValue: [] },
  );
  label = input('');
  selectedItems = signal<MultiselectItem[]>([]);
  operatorCommand = signal<[FilterSuffix | undefined, FilterOperator]>([
    undefined,
    FilterOperator.IN,
  ]);
  rawOperatorCommand = computed(() => {
    const [suffix, operator] = this.operatorCommand() ?? [
      undefined,
      FilterOperator.IN,
    ];
    return suffix === undefined
      ? operator
      : ([suffix, operator] as [FilterSuffix, FilterOperator]);
  });

  constructor(private readonly filterService: FilterService) {}

  ngOnInit() {
    const filter = this.appFilter();
    const operator = this.filterService.getFilterOperator(
      filter,
      this.appFilterOperators()?.[0],
    );
    this.operatorCommand.set(
      Array.isArray(operator) ? operator : [undefined, operator],
    );
    const initialState = this.filterService.getFilterState(filter);
    this.selectedItems.set(initialState ?? []);
  }

  setItemsFilter() {
    const filter = this.appFilter();
    const items = this.selectedItems();
    const [suffix, operator] = this.operatorCommand();
    if (items.length) {
      this.filterService.setFilter(
        filter,
        items.map((item) => item.id),
        operator,
        suffix,
      );
    } else {
      this.filterService.setFilter(filter, undefined);
    }
    this.filterService.storeState(filter, items);
  }

  onScroll(container: HTMLDivElement) {
    const scrollPosition = container.scrollTop + container.clientHeight;
    const scrollHeight = container.scrollHeight;

    // Trigger at 50% scroll
    if (
      scrollPosition >= scrollHeight * 0.8 &&
      !this.loading() &&
      this.hasNextPage()
    ) {
      this.searchPage.update((page) => page + 1);
    }
  }

  toggleItem(item: MultiselectItem) {
    this.selectedItems.update((items) => {
      const idx = items.findIndex((i) => i.id === item.id);
      if (idx !== -1) {
        items.splice(idx, 1);
      } else {
        items.push(item);
      }
      return [...items];
    });
    this.setItemsFilter();
  }

  isItemChecked(items: MultiselectItem[], item: MultiselectItem) {
    return !!items.find((i) => i.id === item.id);
  }

  selectAll() {
    this.selectedItems.update((items) => {
      const searchItems = this.searchItems();
      const ids = new Set(items.map((i) => i.id));
      for (const item of searchItems) {
        if (!ids.has(item.id)) items.push(item);
      }
      return [...items];
    });
    this.setItemsFilter();
  }

  deselectAll() {
    this.selectedItems.update((items) => {
      const searchItems = new Set(this.searchItems().map((i) => i.id));
      return items.filter((i) => !searchItems.has(i.id));
    });
    this.setItemsFilter();
  }

  setOperator($event: FilterCommand) {
    this.operatorCommand.set(
      Array.isArray($event)
        ? $event
        : [undefined as unknown as FilterSuffix, $event],
    );
    this.setItemsFilter();
  }
}
